import { useState } from 'react'
import {
  User,
  ShoppingBag,
  MapPin,
  MessageSquarePlus,
  ShoppingCart,
  ChevronDown,
  ChevronUp,
  Store,
  Truck,
  CheckCircle2,
  Bot,
  XCircle,
  Calendar,
} from 'lucide-react'
import type { ConversationSalesContext } from '@/services/conversationsApi'
import { hasConfirmedProduct } from '@/pages/conversations/conversationUx'
import { normalizeToVertical } from '@/lib/verticalUI'

export interface HandoffCommercialCardProps {
  conversationId: string
  salesContext?: ConversationSalesContext | null
  agentName?: string
  businessType?: string | null
  onCreateOrder: () => void
  onPrefillMessage: (text: string) => void
  botSummaryText?: string
  onResumeBot?: () => void
  onRequestClose?: () => void
}

export function HandoffCommercialCard({
  conversationId: _conversationId,
  salesContext,
  agentName = 'del equipo de ventas',
  businessType,
  onCreateOrder,
  onPrefillMessage,
  botSummaryText,
  onResumeBot,
  onRequestClose,
}: HandoffCommercialCardProps) {
  const [showFullNote, setShowFullNote] = useState(false)

  if (!salesContext) return null

  const vertical = normalizeToVertical(businessType)
  const isAppointmentVertical = ['salud', 'servicios', 'belleza_estetica'].includes(vertical)
  const isInmobiliaria = vertical === 'inmobiliaria'
  const isTurismo = vertical === 'turismo_posadas'

  const handoff = salesContext.handoff
  const draft = salesContext.activeOrderDraft
  const isProductConfirmed = hasConfirmedProduct(salesContext)

  // Si no hay datos de traspaso ni borrador de orden, no renderizamos
  if (!handoff && !draft?.items?.length && !salesContext.product?.title) {
    return null
  }

  const customerName = handoff?.customerName || 'Cliente'
  const isPickup = handoff?.fulfillmentMethod === 'pickup' || draft?.fulfillmentMethod === 'pickup'
  const address = handoff?.fulfillmentAddress || draft?.fulfillmentAddress

  const items = handoff?.items?.length
    ? handoff.items
    : draft?.items?.length
      ? draft.items.map((it) => ({
          name: it.title || 'Producto',
          quantity: it.quantity || 1,
          price: it.unitPriceCents ? it.unitPriceCents / 100 : undefined,
        }))
      : salesContext.product?.title
        ? [{ name: salesContext.product.title, quantity: 1, price: salesContext.product.price }]
        : []

  const totalAmount =
    handoff?.totalAmount ??
    (draft?.totalCents ? draft.totalCents / 100 : undefined) ??
    (salesContext.product?.price ? Number(salesContext.product.price) : undefined)

  const formattedTotal = totalAmount != null ? `$${totalAmount.toFixed(2)}` : ''
  const primaryItemName = items[0]?.name || 'tu pedido'

  const hasConfirmedOrder = Boolean(
    botSummaryText?.includes('🎉 ¡PEDIDO / VENTA CONFIRMADA!') ||
    botSummaryText?.includes('PEDIDO CONFIRMADO') ||
    draft?.items?.some((it: any) => it.status === 'confirmed')
  )
  const orderNumberMatch = botSummaryText?.match(/• Orden:\s*#?([A-Za-z0-9_-]+)/i)
  const orderNumber = orderNumberMatch ? orderNumberMatch[1] : null

  // Genera el saludo empático y directo según la vertical
  const handleLoadClosingGreeting = () => {
    const greetingName = customerName !== 'Cliente' ? `¡Hola ${customerName}!` : '¡Hola!'

    let text = ''
    if (isAppointmentVertical) {
      text = `${greetingName} Te escribe ${agentName}. Recibí tu solicitud para ${primaryItemName}${
        formattedTotal ? ` (${formattedTotal})` : ''
      }. Con gusto coordinamos tu cita y disponibilidad de horario. ¿Deseas agendar para esta semana?`
    } else if (isInmobiliaria) {
      text = `${greetingName} Te escribe ${agentName}. Recibí tu interés en la propiedad ${primaryItemName}. Con gusto coordinamos una visita presencial o te comparto la ficha técnica detallada.`
    } else if (isTurismo) {
      text = `${greetingName} Te escribe ${agentName}. Recibí tu solicitud de reserva para ${primaryItemName}${
        formattedTotal ? ` (${formattedTotal})` : ''
      }. ¿Para cuántas personas y qué fechas tentativas tienes pensadas?`
    } else {
      const methodText = isPickup
        ? 'retiro en tienda'
        : address
          ? `entrega por delivery a ${address}`
          : 'entrega por delivery'

      text = `${greetingName} Te escribe ${agentName}. Ya tengo listo tu pedido de ${primaryItemName}${
        formattedTotal ? ` (${formattedTotal})` : ''
      } para ${methodText}. ¿Deseas pagar en efectivo o prefieres los datos para transferencia / Pago Móvil?`
    }

    onPrefillMessage(text)
  }

  const renderLogisticsBadge = () => {
    if (isAppointmentVertical) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full border border-teal-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-teal-800">
          <Calendar className="h-3 w-3 text-teal-700" />
          <span>Atención / Cita Presencial</span>
        </span>
      )
    }
    if (isInmobiliaria) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-sky-800">
          <Calendar className="h-3 w-3 text-sky-700" />
          <span>Visita a Propiedad</span>
        </span>
      )
    }
    if (isTurismo) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-amber-800">
          <Calendar className="h-3 w-3 text-amber-700" />
          <span>Reserva / Hospedaje</span>
        </span>
      )
    }
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-700">
        {isPickup ? (
          <>
            <Store className="h-3 w-3 text-emerald-700" />
            <span>Retiro en Tienda</span>
          </>
        ) : (
          <>
            <Truck className="h-3 w-3 text-blue-700" />
            <span>Delivery a Domicilio</span>
          </>
        )}
      </span>
    )
  }

  return (
    <div
      role="region"
      aria-label="Ficha de Traspaso Comercial"
      className="mx-auto mb-4 max-w-5xl overflow-hidden rounded-2xl border border-emerald-300/80 bg-gradient-to-br from-emerald-50/90 via-white to-teal-50/60 p-3.5 sm:p-4 shadow-sm backdrop-blur-xs"
    >
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-emerald-100 pb-2.5">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-700 text-white px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider shadow-2xs">
            <CheckCircle2 className="h-3.5 w-3.5" />
            {hasConfirmedOrder
              ? orderNumber
                ? `Pedido Confirmado #${orderNumber} 🎉`
                : 'Pedido Confirmado 🎉'
              : 'Pedido por Concretar'}
          </span>
          {renderLogisticsBadge()}
        </div>

        {formattedTotal && (
          <div className="flex items-center gap-1 font-black text-emerald-950 text-sm sm:text-base">
            <span className="text-[10px] uppercase font-bold text-slate-500">Total:</span>
            <span>{formattedTotal}</span>
          </div>
        )}
      </div>

      {/* Grid de 3 Columnas: Cliente, Pedido, Entrega */}
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2.5 text-xs text-slate-700">
        {/* Columna Cliente */}
        <div className="rounded-xl border border-emerald-100/80 bg-white/80 p-2.5 shadow-2xs">
          <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
            <User className="h-3 w-3 text-emerald-700" />
            <span>Cliente</span>
          </div>
          <p className="font-bold text-slate-900 truncate">{customerName}</p>
          {handoff?.customerPhone && (
            <p className="text-[11px] text-slate-500 font-mono">{handoff.customerPhone}</p>
          )}
        </div>

        {/* Columna Pedido */}
        <div className="rounded-xl border border-emerald-100/80 bg-white/80 p-2.5 shadow-2xs">
          <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
            <ShoppingBag className="h-3 w-3 text-emerald-700" />
            <span>{isAppointmentVertical ? 'Servicio / Consulta' : isInmobiliaria ? 'Inmueble' : isTurismo ? 'Hospedaje' : 'Pedido'}</span>
          </div>
          <div className="space-y-0.5">
            {items.map((it, idx) => (
              <p key={idx} className="font-semibold text-slate-900 truncate">
                {it.quantity}x {it.name}
              </p>
            ))}
          </div>
        </div>

        {/* Columna Entrega / Modalidad */}
        <div className="rounded-xl border border-emerald-100/80 bg-white/80 p-2.5 shadow-2xs">
          <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
            <MapPin className="h-3 w-3 text-emerald-700" />
            <span>{isAppointmentVertical ? 'Modalidad / Sede' : isInmobiliaria ? 'Ubicación' : isTurismo ? 'Destino' : 'Entrega'}</span>
          </div>
          <p className="font-semibold text-slate-900 truncate">
            {isAppointmentVertical
              ? 'Atención en sede / consultorio'
              : isInmobiliaria
                ? 'Visita a inmueble'
                : isTurismo
                  ? 'Estadía / Posada'
                  : isPickup
                    ? 'Retiro en sucursal'
                    : 'Envío a domicilio'}
          </p>
          {address && (
            <p className="text-[11px] text-slate-600 line-clamp-1" title={address}>
              {address}
            </p>
          )}
        </div>
      </div>

      {/* Barra de Acciones de 1 Clic */}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-emerald-100">
        <div className="text-[11px] text-emerald-900 font-medium">
          {handoff?.actionRequired || (hasConfirmedOrder ? 'Venta confirmada por el bot. Coordina el cobro y entrega.' : 'Coordinar método de pago y concretar la venta')}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Botón: Reanudar Bot */}
          {onResumeBot && (
            <button
              type="button"
              onClick={onResumeBot}
              className="inline-flex items-center gap-1 rounded-lg border border-blue-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-blue-700 shadow-2xs hover:bg-blue-50 active:scale-95 transition-all"
              title="Devolver la conversación al bot de ventas"
            >
              <Bot className="h-3.5 w-3.5 text-blue-600" />
              <span>Reanudar Bot</span>
            </button>
          )}

          {/* Botón: Cerrar Conversación */}
          {onRequestClose && (
            <button
              type="button"
              onClick={onRequestClose}
              className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-rose-700 shadow-2xs hover:bg-rose-50 active:scale-95 transition-all"
              title="Cerrar conversación registrando el motivo de cierre"
            >
              <XCircle className="h-3.5 w-3.5 text-rose-600" />
              <span>Cerrar Chat</span>
            </button>
          )}

          {/* Botón: Cargar Saludo de Cobro / Contacto */}
          <button
            type="button"
            onClick={handleLoadClosingGreeting}
            className="inline-flex items-center gap-1 rounded-lg border border-emerald-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-emerald-800 shadow-2xs hover:bg-emerald-50 active:scale-95 transition-all"
            title="Cargar mensaje contextual listo en el chat"
          >
            <MessageSquarePlus className="h-3.5 w-3.5 text-emerald-600" />
            <span>
              {isAppointmentVertical
                ? 'Cargar saludo de cita'
                : isInmobiliaria
                  ? 'Cargar saludo de visita'
                  : isTurismo
                    ? 'Cargar saludo de reserva'
                    : 'Cargar saludo de cobro'}
            </span>
          </button>

          {/* Botón: Crear o Gestionar Orden */}
          <button
            type="button"
            onClick={onCreateOrder}
            disabled={!isProductConfirmed && !hasConfirmedOrder}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#075f55] px-3 py-1.5 text-xs font-bold text-white shadow-xs hover:bg-[#064e46] active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            title={
              hasConfirmedOrder
                ? 'Ver o gestionar orden comercial'
                : isProductConfirmed
                  ? 'Emitir orden comercial con datos pre-poblados'
                  : 'Se requiere un producto confirmado'
            }
          >
            <ShoppingCart className="h-3.5 w-3.5" />
            <span>
              {isAppointmentVertical
                ? 'Gestionar Cita'
                : hasConfirmedOrder
                  ? 'Ver / Facturar Orden'
                  : 'Crear Orden'} {formattedTotal ? `(${formattedTotal})` : ''}
            </span>
          </button>
        </div>
      </div>

      {/* Detalle técnico expandible opcional */}
      {botSummaryText && (
        <div className="mt-2 pt-1">
          <button
            type="button"
            onClick={() => setShowFullNote((prev) => !prev)}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-slate-800 transition-colors"
          >
            <span>{showFullNote ? 'Ocultar bitácora IA' : 'Ver bitácora técnica de la IA'}</span>
            {showFullNote ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
          {showFullNote && (
            <div className="mt-1.5 rounded-lg bg-slate-50 p-2.5 text-xs text-slate-600 font-mono whitespace-pre-wrap border border-slate-200/70 max-h-48 overflow-y-auto">
              {botSummaryText}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
